import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "../styles/SubmitApplication.css";

import { db } from "../firebase/firebase";
import { collection, addDoc, doc, updateDoc } from "firebase/firestore";

import { formFields, validateForm, defaultFormData } from "../utils/formConfig";
import JobApplicationFields from "../components/JobApplicationFields";

import { useDispatch, useSelector } from "react-redux";
import {
  addUserApplication,
  updateUserApplication,
} from "../redux/form/formActions";

import axios from "axios";
import emailjs from "@emailjs/browser";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";

const SubmitApplication = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.currentUser);

  const editData = location.state?.application || null;
  const isEdit = location.state?.isEdit || false;

  const [formData, setFormData] = useState({
    ...defaultFormData,
    ...(editData || {}),
    email: user?.email || "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "file" ? files[0] : value,
    }));
  };

  const uploadToCloudinary = async (file) => {
    const cloudForm = new FormData();
    cloudForm.append("file", file);
    cloudForm.append(
      "upload_preset",
      process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET
    );

    try {
      const res = await axios.post(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/auto/upload`,
        cloudForm
      );
      return res.data.secure_url;
    } catch (error) {
      console.error("❌ Cloudinary upload failed:", error);
      return null;
    }
  };

  const sendConfirmationEmail = async (data) => {
    const emailParams = {
      user_name: `${data.firstName} ${data.lastName}`,
      user_email: data.email,
      job_position: data.jobTitle || "Job",
    };

    try {
      await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        emailParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      console.log("✅ Email sent successfully");
    } catch (err) {
      console.error("❌ EmailJS Error:", err);
    }
  };

  const handleConfirmUpdate = async () => {
    setShowModal(false);
    setLoading(true);

    let resumeUrl = formData.resume;

    if (formData.resume instanceof File) {
      resumeUrl = await uploadToCloudinary(formData.resume);
      if (!resumeUrl) {
        toast.error("Failed to upload resume. Please try again.");
        setLoading(false);
        return;
      }
    }

    const dataToStore = {
      ...formData,
      resume: resumeUrl,
      uid: user?.uid || "",
      timestamp: new Date().toISOString(),
    };

    try {
      const docRef = doc(db, "formResponses", editData.id);
      await updateDoc(docRef, dataToStore);
      dispatch(
        updateUserApplication({ id: editData.id, updatedData: dataToStore })
      );

      toast.success("✅ Application updated successfully!");
      navigate("/my-application");
    } catch (error) {
      console.error("❌ Firestore error:", error);
      toast.error("Failed to update your application.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isEdit && editData?.id) {
      setShowModal(true);
    } else {
      setLoading(true);

      let resumeUrl = formData.resume;

      if (formData.resume instanceof File) {
        resumeUrl = await uploadToCloudinary(formData.resume);
        if (!resumeUrl) {
          toast.error("Failed to upload resume. Please try again.");
          setLoading(false);
          return;
        }
      }

      const dataToStore = {
        ...formData,
        resume: resumeUrl,
        uid: user?.uid || "",
        timestamp: new Date().toISOString(),
      };

      try {
        const docRef = await addDoc(
          collection(db, "formResponses"),
          dataToStore
        );
        const newApplication = { ...dataToStore, id: docRef.id };
        dispatch(addUserApplication(newApplication));

        await sendConfirmationEmail(dataToStore);

        toast.success("✅ Application submitted successfully!");
        navigate("/success-page");
      } catch (error) {
        console.error("❌ Firestore error:", error);
        toast.error("Failed to save your application.");
      } finally {
        setLoading(false);
        setFormData({
          ...defaultFormData,
          email: user?.email || "",
        });
        setErrors({});
      }
    }
  };

  return (
    <div
      style={{
        background: "linear-gradient(135deg, #1e3c72, #2a5298)",
        minHeight: "100vh",
        padding: "3rem 1rem",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        color: "#fff",
        position: "relative",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        backdrop="static"
      >
        <div className="custom-modal-style">
          <Modal.Header closeButton>
            <Modal.Title className="w-100 text-center">
              Confirm Update
            </Modal.Title>
          </Modal.Header>

          <Modal.Body className="text-center">
            Are you sure you want to update your application?
          </Modal.Body>

          <Modal.Footer className="d-flex justify-content-center">
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirmUpdate}>
              Yes, Update
            </Button>
          </Modal.Footer>
        </div>
      </Modal>

      <div
        className="shadow-lg p-4"
        style={{
          maxWidth: "900px",
          width: "100%",
          background: "#ffffff10",
          border: "1px solid #ffffff20",
          borderRadius: "20px",
          backdropFilter: "blur(10px)",
        }}
      >
        <h2 className="text-center mb-3 fw-bold" style={{ color: "#81d4fa" }}>
          📝 {isEdit ? "Edit Your Application" : "Apply for Your Dream Job"}
        </h2>

        <p className="text-center mb-4" style={{ color: "#bbdefb" }}>
          Fill in your details to {isEdit ? "update" : "submit"} your
          application
        </p>

        <form className="row g-4" onSubmit={handleSubmit} noValidate>
          <JobApplicationFields
            formFields={formFields}
            formData={formData}
            errors={errors}
            handleChange={handleChange}
          />
          <div className="col-12">
            <button
              type="submit"
              className="btn w-100 py-2 fw-semibold"
              disabled={loading}
              style={{
                background: "linear-gradient(45deg, #29b6f6, #66bb6a, #ffa726)",
                color: "#fff",
                border: "none",
                borderRadius: "10px",
              }}
            >
              {loading
                ? "⏳ Submitting..."
                : isEdit
                ? "Update Application"
                : "Submit Application"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SubmitApplication;
